import { RaftOKFail, RaftSysModInfoWiFi, RaftWifiConnState, RaftWifiConnStatus, RaftWifiScanResults } from "@robotical/raftjs";
import { AppSentMessage } from "../../../types/communication-between-apps/wrapper-communication";
import Marty from "./Marty";
import Logger from "../../../services/logger/Logger";

const SHOW_LOGS = true;
const TAG = "MartyWifiManager";

export class MartyWifiManager {

    private _wifiConnStatus: RaftWifiConnStatus = new RaftWifiConnStatus();
    private _defaultWiFiHostname = "Raft";
    private _maxSecsToWaitForWiFiConn = 20;

    private _wifiScanDuration = 10000; // ms

    constructor(public marty: Marty) {
        this.marty = marty;
    }

    _getHostnameFromFriendlyName(): string {
        const friendlyName = this.marty.getFriendlyName();
        if (!friendlyName) {
            return this._defaultWiFiHostname;
        }
        let hostname = friendlyName;
        hostname = hostname?.replace(/ /g, "-");
        hostname = hostname.replace(/\W+/g, "");
        return hostname;
    }

    async wifiConnect(ssid: string, password: string): Promise<boolean> {
        Logger.info(SHOW_LOGS, TAG, `RaftSystemUtils Connect to WiFi ${ssid} password ${password}`);

        // Issue the command to connect WiFi
        try {
            const RaftRESTURL_wifiCredentials =
                "w/" +
                ssid +
                "/" +
                password +
                "/" +
                this._getHostnameFromFriendlyName();
            Logger.info(SHOW_LOGS, TAG,
                `wifiConnect attempting to connect to wifi ${RaftRESTURL_wifiCredentials}`
            );

            await this.marty.sendRestMessage(
                RaftRESTURL_wifiCredentials
            );
        } catch (error) {
            Logger.info(SHOW_LOGS, TAG, `RaftSystemUtils wifiConnect failed ${error}`);
            return false;
        }

        // Wait until connected, timed-out or failed
        for (
            let timeoutCount = 0;
            timeoutCount < this._maxSecsToWaitForWiFiConn;
            timeoutCount++
        ) {
            // Wait a little before checking
            await new Promise((resolve) => setTimeout(resolve, 1000));

            // Get status info
            const connStat = await this.getWiFiConnStatus();
            Logger.info(SHOW_LOGS, TAG, `RaftSystemUtils wifiConnect connStat ${connStat}`);
            if (connStat) {
                return true;
            }
        }
        return false;
    }

    async wifiDisconnect(): Promise<boolean> {
        try {
            Logger.info(SHOW_LOGS, TAG, `RaftSystemUtils wifiDisconnect clearing wifi info`);

            await this.marty.sendRestMessage("wc");
            await new Promise((resolve) => setTimeout(resolve, 2000));
            await this.getWiFiConnStatus();
            return true;
        } catch (error) {
            Logger.info(SHOW_LOGS, TAG, `RaftSystemUtils wifiDisconnect clearing unsuccessful ${error}`);
        }
        return false;
    }

    async getWiFiConnStatus(): Promise<boolean | null> {
        try {
            // Get status
            const ricSysModInfoWiFi = await this.marty.sendRestMessage("sysmodinfo/NetMan") as RaftSysModInfoWiFi;

            Logger.info(SHOW_LOGS, TAG,
                `wifiConnStatus rslt ${ricSysModInfoWiFi.rslt} isConn ${ricSysModInfoWiFi.isConn} paused ${ricSysModInfoWiFi.isPaused}`
            );

            // Check status indicates WiFi connected
            if (ricSysModInfoWiFi.rslt === "ok") {
                this._wifiConnStatus.connState =
                    ricSysModInfoWiFi.isConn !== 0
                        ? RaftWifiConnState.WIFI_CONN_CONNECTED
                        : RaftWifiConnState.WIFI_CONN_NONE;
                this._wifiConnStatus.isPaused = ricSysModInfoWiFi.isPaused !== 0;
                this._wifiConnStatus.ipAddress = ricSysModInfoWiFi.IP;
                this._wifiConnStatus.hostname = ricSysModInfoWiFi.Hostname;
                this._wifiConnStatus.ssid = ricSysModInfoWiFi.SSID;
                this._wifiConnStatus.bssid = ricSysModInfoWiFi.WiFiMAC;
                this._wifiConnStatus.validMs = Date.now();
                return (
                    ricSysModInfoWiFi.isConn !== 0 || ricSysModInfoWiFi.isPaused !== 0
                );
            }
        } catch (error) {
            Logger.info(SHOW_LOGS, TAG, `[DEBUG]: wifiConnStatus sysmodinfo failed ${error}`);
            this._wifiConnStatus.validMs = 0;
        }
        this._wifiConnStatus.connState = RaftWifiConnState.WIFI_CONN_NONE;
        this._wifiConnStatus.isPaused = false;
        return null;
    }

    async pauseWifiConnection(pause: boolean) {
        try {
            if (pause) {
                await this.marty.sendRestMessage("wifipause/pause");
            } else {
                await this.marty.sendRestMessage("wifipause/resume");
            }
            return true;
        } catch (error) {
            Logger.info(SHOW_LOGS, TAG, `RaftSystemUtils wifiConnect wifi pause ${error}`);
            return false;
        }
    }

    async _wifiScanResults(): Promise<boolean | RaftOKFail | RaftWifiScanResults> {
        try {
            Logger.info(SHOW_LOGS, TAG, `RaftSystemUtils wifiScanResults`);
            return this.marty.sendRestMessage(
                "wifiscan/results"
            ) as Promise<RaftOKFail | RaftWifiScanResults>;
        } catch (error) {
            Logger.info(SHOW_LOGS, TAG, `RaftSystemUtils wifiScanResults unsuccessful ${error}`);
        }
        return false;
    }

    async _wifiScanStart(): Promise<boolean> {
        try {
            Logger.info(SHOW_LOGS, TAG, `RaftSystemUtils _wifiScanStart`);
            await this.marty.sendRestMessage("wifiscan/start");
            return true;
        } catch (error) {
            Logger.info(SHOW_LOGS, TAG, `RaftSystemUtils _wifiScanStart unsuccessful ${error}`);
        }
        return false;
    }

    getCachedWifiStatus(): RaftWifiConnStatus {
        return this._wifiConnStatus;
    }

    async getWifiScanResults(): Promise<boolean | RaftOKFail | RaftWifiScanResults> {
        await this._wifiScanStart();
        let resultsTimeout: NodeJS.Timeout;
        const results: RaftOKFail | RaftWifiScanResults = await new Promise(
            (resolve, reject) =>
            (resultsTimeout = setTimeout(() => {
                this._wifiScanResults()
                    .then((wifiscanMsgResults: boolean | RaftOKFail | RaftWifiScanResults) => {
                        if (typeof wifiscanMsgResults !== "boolean") {
                            resolve(wifiscanMsgResults);
                        } else {
                            reject("Something went wrong");
                        }
                    })
                    .catch((err: unknown) => reject(err))
                    .finally(() => clearTimeout(resultsTimeout));
            }, this._wifiScanDuration))
        );
        if ((results as RaftWifiScanResults).hasOwnProperty("wifi")) {
            return results as RaftWifiScanResults;
        }
        return false;
    }
}
